import type { FlowSpec } from '@digibee/flow';

import { resolveMethodIdFromBlockExecutionComponentId } from '~/scenes/Build/scenes/FlowBuilder/components/Design/panels/Endpoints/blockExecutionLevelUtils';
import { useCanvasStore } from '~/scenes/Build/scenes/FlowBuilder/stores/canvasStore';
import {
  type ComponentConfig,
  isChoiceComponentConfig,
  useComponentConfigStore
} from '~/scenes/Build/scenes/FlowBuilder/stores/componentConfigStore';
import type { Track } from '~/scenes/Build/scenes/FlowBuilder/stores/trackStore';
import { useTrackStore } from '~/scenes/Build/scenes/FlowBuilder/stores/trackStore';

const API_FIRST = 'api-first:';
const API_FIRST_CHOICE_PREFIX = `${API_FIRST}choice:`;
const API_FIRST_NOT_FOUND_PREFIX = `${API_FIRST}not-found:`;

export function isApiFirstRouteTrackKey(key: string): boolean {
  if (!key.startsWith(API_FIRST)) {
    return false;
  }
  if (
    key.startsWith(API_FIRST_CHOICE_PREFIX) ||
    key.startsWith(API_FIRST_NOT_FOUND_PREFIX)
  ) {
    return false;
  }
  if (key.endsWith('-onProcessTrack') || key.endsWith('-onExceptionTrack')) {
    return false;
  }
  if (key.includes(':success-response:') || key.includes(':error-response:')) {
    return false;
  }
  if (key.includes(`${API_FIRST}block-execution:`)) {
    return false;
  }
  return true;
}

function methodIdFromRouteTrackName(trackName: string): string | undefined {
  if (!isApiFirstRouteTrackKey(trackName)) {
    return undefined;
  }
  const methodId = trackName.slice(API_FIRST.length).trim();
  return methodId || undefined;
}

function resolveMethodIdFromFlowSpecTarget(
  spec: Record<string, unknown>,
  targetTrackName: string
): string | undefined {
  const direct = methodIdFromRouteTrackName(targetTrackName);
  if (direct) {
    return direct;
  }

  const trackVal = spec[targetTrackName];
  if (!Array.isArray(trackVal) || trackVal.length === 0) {
    return undefined;
  }

  const first = trackVal[0];
  if (!first || typeof first !== 'object') {
    return undefined;
  }

  const componentId = (first as { id?: string }).id;
  if (!componentId) {
    return undefined;
  }

  const match = Object.entries(spec).find(([trackName, track]) => {
    const methodId = methodIdFromRouteTrackName(trackName);
    if (!methodId) {
      return false;
    }
    const arr = track as unknown[];
    return (
      Array.isArray(arr) &&
      (arr[0] as { id?: string } | undefined)?.id === componentId
    );
  });

  return match ? methodIdFromRouteTrackName(match[0]) : undefined;
}

function findApiRouterInFlowSpecStart(
  spec: Record<string, unknown>
): { when?: Array<{ target?: string }> } | undefined {
  const { start } = spec;
  if (!Array.isArray(start)) {
    return undefined;
  }

  return start.find(
    (
      component
    ): component is { id: string; when?: Array<{ target?: string }> } =>
      typeof component === 'object' &&
      component !== null &&
      typeof (component as { id?: string }).id === 'string' &&
      (component as { id: string }).id.startsWith(API_FIRST_CHOICE_PREFIX)
  );
}

export function flowSpecHasApiRouter(flowSpec: unknown): boolean {
  if (!flowSpec || typeof flowSpec !== 'object') {
    return false;
  }
  return (
    findApiRouterInFlowSpecStart(flowSpec as Record<string, unknown>) !==
    undefined
  );
}

export function collectConnectedApiMethodIdsFromFlowSpec(
  flowSpec: unknown
): Set<string> {
  if (!flowSpec || typeof flowSpec !== 'object') {
    return new Set();
  }

  const spec = flowSpec as Record<string, unknown>;
  const router = findApiRouterInFlowSpecStart(spec);
  if (!router?.when?.length) {
    return new Set();
  }

  const methodIds = router.when
    .map(condition => {
      const target = condition?.target;
      if (!target || target.startsWith(API_FIRST_NOT_FOUND_PREFIX)) {
        return undefined;
      }
      return resolveMethodIdFromFlowSpecTarget(spec, target);
    })
    .filter((methodId): methodId is string => Boolean(methodId));

  return new Set(methodIds);
}

export function findApiRouterComponentId(
  tracks: Map<string, Track>,
  configurations: Map<string, ComponentConfig>
): string | undefined {
  const startTrack = tracks.get('start');
  if (!startTrack) {
    return undefined;
  }

  return startTrack.componentIds.find(componentId => {
    const config = configurations.get(componentId);
    if (!config) {
      return false;
    }
    return (
      isChoiceComponentConfig(config) &&
      componentId.startsWith(API_FIRST_CHOICE_PREFIX)
    );
  });
}

export function canvasStoresHaveApiRouter(): boolean {
  const { tracks } = useTrackStore.getState();
  const { configurations } = useComponentConfigStore.getState();
  return findApiRouterComponentId(tracks, configurations) !== undefined;
}

export function collectConnectedApiMethodIdsFromCanvasStores(): Set<string> {
  const { getEdgesBySource } = useCanvasStore.getState();
  const { tracks } = useTrackStore.getState();
  const { configurations } = useComponentConfigStore.getState();

  const routerId = findApiRouterComponentId(tracks, configurations);
  if (!routerId) {
    return new Set();
  }

  const methodIds = getEdgesBySource(routerId)
    .filter(
      edge =>
        edge.metadata?.conditionType !== 'otherwise' &&
        edge.tags.includes('choice')
    )
    .map(edge => resolveMethodIdFromBlockExecutionComponentId(edge.targetId))
    .filter((methodId): methodId is string => Boolean(methodId));

  return new Set(methodIds);
}

export type FlowSpecLike = FlowSpec | Record<string, unknown>;
